import { Assert, Deferred, Value, pipe } from '@helo/utils';
import { isEqual, merge } from 'lodash';
import { StateConfigUtils } from '../StateConfig/StateConfig.utils';
import { schemaToDefinitions } from '../TypeDefinitions/schemaToDefinitions';
import { mapInputObjectConfig } from './DataTypeConfig-input-mapper';
import { createDataTypeConfigFromMapper, createFieldMapperConfig, createInputObjectMapperConfig, createInputValueMapperConfig, createObjectMapperConfig, mapDataTypeConfigLazy, } from './DataTypeConfig-mapper';
import { validateData } from './DataTypeConfig.validate';
import { toZodSchema } from './DataTypeConfig.zod';
export const mapDataTypeConfigChildren = (mapper) => (config) => {
    return {
        ...config,
        ...('args' in config && { args: config.args.map(mapper) }),
        ...('fields' in config && { fields: config.fields.map(mapper) }),
        ...('possibleTypes' in config && {
            possibleTypes: config.possibleTypes.map(mapper),
        }),
        ...('ofType' in config && { ofType: mapper(config.ofType) }),
        ...('items' in config &&
            config.items && { items: config.items.map(mapper) }),
    };
};
const getDataTypeSelectorPointer = (config) => {
    if ('kind' in config) {
        return config.kind === 'FIELD' || config.kind === 'INPUT_VALUE'
            ? ['key', config.key]
            : config.kind === 'ENUM_VALUE'
                ? ['name', config.name]
                : ['typeName', config.typeName];
    }
    if ('key' in config && config.key) {
        return ['key', config.key];
    }
    if ('name' in config && config.name) {
        return ['name', config.name];
    }
    return ['typeName', config.typeName];
};
const applyDataTypeSelectorPaths = (parentConfigPath = []) => (config) => {
    if (Array.isArray(config)) {
        const newItems = config.map(applyDataTypeSelectorPaths(parentConfigPath));
        return newItems;
    }
    const currentConfigPath = [
        ...parentConfigPath,
        getDataTypeSelectorPointer(config),
    ];
    const newConfig = {
        ...config,
        meta: {
            path: currentConfigPath,
            unmappedPath: currentConfigPath,
            ...config.meta,
        },
    };
    return mapDataTypeConfigChildren(applyDataTypeSelectorPaths(currentConfigPath))(newConfig);
};
export const configMatchesMatcher = (currentMatcher) => (config) => {
    const [matcherType, matcherValue] = currentMatcher;
    if (matcherType === 'key' && 'key' in config) {
        return config.key === matcherValue;
    }
    if (matcherType === 'typeName' && 'typeName' in config) {
        return config.typeName === matcherValue;
    }
    if (matcherType === 'kind' && 'kind' in config) {
        return (config.kind.toUpperCase() ===
            matcherValue.toUpperCase());
    }
    if (matcherType === 'name' && 'name' in config) {
        return config.name === matcherValue;
    }
    if (matcherType === 'dataMatch') {
        throw new Error('dataMatch matcher not supported for DataTypeConfigs');
    }
    if (matcherType === 'index') {
        return false;
    }
    return false;
};
const excludeDataMatchMatcher = (locator) => {
    return locator.filter(matcher => matcher[0] !== 'dataMatch');
};
const getDataTypeConfigMatcherCacheKey = ([matcherType, matcherValue,]) => {
    const value = typeof matcherValue === 'string' || typeof matcherValue === 'number'
        ? matcherValue
        : JSON.stringify(matcherValue);
    return `${matcherType}:${value}`;
};
export const getDataTypeConfigLocatorCacheKey = (locator) => locator.map(getDataTypeConfigMatcherCacheKey).join(',');
const getDataTypeConfigSearchRootCacheKey = (configsToSearch, allDataTypeConfigs) => {
    const initialConfigs = Array.isArray(configsToSearch)
        ? configsToSearch
        : [configsToSearch];
    if (Array.isArray(configsToSearch) &&
        configsToSearch === allDataTypeConfigs) {
        return 'global';
    }
    if (initialConfigs.length === 1) {
        const config = initialConfigs[0];
        if ('typeName' in config && typeof config.typeName === 'string') {
            return `root:${config.typeName}`;
        }
        if ('key' in config && typeof config.key === 'string') {
            return `root:field:${config.key}`;
        }
        if ('kind' in config && typeof config.kind === 'string') {
            return `root:kind:${config.kind}`;
        }
    }
    return `roots:${initialConfigs.length}`;
};
export const getDataTypeConfigFindCacheKey = (locator, configsToSearch, allDataTypeConfigs = []) => `${getDataTypeConfigSearchRootCacheKey(configsToSearch, allDataTypeConfigs)}|${getDataTypeConfigLocatorCacheKey(locator)}`;
export const findDataTypeConfigBreadthFirst = (inputLocator, allDataTypeConfigs = [], cache = {}) => (configsToSearch) => {
    const locator = excludeDataMatchMatcher(inputLocator);
    if (!locator || locator.length === 0) {
        throw new Error(`Unable to find config for locator: ${JSON.stringify(locator)}`);
    }
    const cacheKey = getDataTypeConfigFindCacheKey(locator, configsToSearch, allDataTypeConfigs);
    if (Object.hasOwn(cache, cacheKey)) {
        const cached = cache[cacheKey];
        if (cached === null) {
            throw new Error(`Unable to find config for locator: ${JSON.stringify(locator)}`);
        }
        return cached;
    }
    const initialConfigs = Array.isArray(configsToSearch)
        ? configsToSearch
        : [configsToSearch];
    const resolveTypeReference = (config) => {
        if ('kind' in config && config.kind === 'TYPE_REFERENCE') {
            const resolved = allDataTypeConfigs.find(c => c.typeName === config.typeName);
            return resolved ?? config;
        }
        return config;
    };
    const hasDirectFields = (config) => {
        return 'fields' in config && Array.isArray(config.fields);
    };
    const getDirectFields = (config) => {
        if (hasDirectFields(config)) {
            return config.fields;
        }
        return [];
    };
    const getContainerChildren = (config) => {
        if ('items' in config &&
            Array.isArray(config.items) &&
            config.items.length > 0) {
            return config.items;
        }
        if ('ofType' in config && config.ofType) {
            return [config.ofType];
        }
        if ('possibleTypes' in config && Array.isArray(config.possibleTypes)) {
            return config.possibleTypes;
        }
        if ('enumValues' in config && Array.isArray(config.enumValues)) {
            return config.enumValues;
        }
        return [];
    };
    const findInDirectFields = (config, locatorIndex) => {
        const currentMatcher = locator[locatorIndex];
        for (const field of getDirectFields(config)) {
            const resolvedField = resolveTypeReference(field);
            const isMatch = configMatchesMatcher(currentMatcher)(resolvedField);
            if (!isMatch) {
                continue;
            }
            if (locatorIndex === locator.length - 1) {
                return resolvedField;
            }
            const result = findInChildren(resolvedField, locatorIndex + 1);
            if (result) {
                return result;
            }
        }
        return null;
    };
    const findInDirectFieldContainers = (config, locatorIndex) => {
        const fields = getDirectFields(config);
        if (fields.length !== 1) {
            return null;
        }
        const resolvedField = resolveTypeReference(fields[0]);
        if (hasDirectFields(resolvedField)) {
            return null;
        }
        return findInContainerChildren(resolvedField, locatorIndex);
    };
    const findInContainerChildren = (config, locatorIndex) => {
        for (const child of getContainerChildren(config)) {
            const result = findConfig(child, locatorIndex);
            if (result) {
                return result;
            }
        }
        return null;
    };
    const findInChildren = (config, locatorIndex) => {
        if (hasDirectFields(config)) {
            return (findInDirectFields(config, locatorIndex) ??
                findInDirectFieldContainers(config, locatorIndex));
        }
        return findInContainerChildren(config, locatorIndex);
    };
    const findConfig = (currentConfig, locatorIndex) => {
        const resolvedConfig = resolveTypeReference(currentConfig);
        const currentMatcher = locator[locatorIndex];
        if (currentMatcher[0] === 'index' &&
            'items' in resolvedConfig &&
            Array.isArray(resolvedConfig.items)) {
            const targetItem = resolvedConfig.items[currentMatcher[1]];
            if (!targetItem) {
                return null;
            }
            if (locatorIndex === locator.length - 1) {
                return targetItem;
            }
            return findConfig(targetItem, locatorIndex + 1);
        }
        const isMatch = configMatchesMatcher(currentMatcher)(resolvedConfig);
        if (isMatch) {
            if (locatorIndex === locator.length - 1) {
                return resolvedConfig;
            }
            return findInChildren(resolvedConfig, locatorIndex + 1);
        }
        const shouldSearchChildrenOnMiss = !('kind' in resolvedConfig) ||
            (resolvedConfig.kind !== 'FIELD' &&
                resolvedConfig.kind !== 'INPUT_VALUE')
            ? true
            : currentMatcher[0] !== 'key' || locatorIndex > 0;
        if (!shouldSearchChildrenOnMiss) {
            return null;
        }
        return findInChildren(resolvedConfig, locatorIndex);
    };
    for (const config of initialConfigs) {
        if (config) {
            const result = findConfig(config, 0);
            if (result) {
                cache[cacheKey] = result;
                return result;
            }
        }
    }
    cache[cacheKey] = null;
    throw new Error(`Unable to find config for locator: ${JSON.stringify(locator)}`);
};
const getDataTypeConfigField = (key) => (config) => {
    if (!config) {
        throw new Error('No config found');
    }
    if (config.kind !== 'OBJECT' || !('fields' in config)) {
        throw new Error('No fields found');
    }
    const foundField = config.fields.find(field => field.key === key);
    if (!foundField) {
        throw new Error(`No field found with key '${key}'`);
    }
    return foundField;
};
const getDataTypeConfigOfType = () => (config) => {
    if (!config) {
        throw new Error('No config found');
    }
    if (config && 'ofType' in config) {
        return config.ofType;
    }
    throw new Error('No ofType found');
};
const listMerge = (matcher, listA, listB, mergeFunction = merge) => {
    if (!listA?.length) {
        return listB ?? listA;
    }
    if (!listB?.length) {
        return listA ?? listB;
    }
    const mergedList = [...listA, ...listB].reduce((acc, item) => {
        const existingItems = acc.filter(i => matcher(i, item));
        if (!existingItems.length) {
            return [...acc, item];
        }
        return [
            ...acc.filter(i => !matcher(i, item)),
            existingItems.reduce((final, current) => {
                return mergeFunction(current, final);
            }, item),
        ];
    }, []);
    return mergedList;
};
const overwriteState = (configA, configB) => {
    if (!configA) {
        return configB;
    }
    if (!configB) {
        return configA;
    }
    const state = Deferred.merge(configB.state ?? {})(configA.state ?? {});
    const fields = listMerge((a, b) => a.key === b.key, configA.fields, listMerge((a, b) => configB.typeName === 'ALLTYPES', configA.fields, configB.fields, overwriteState), overwriteState);
    const args = listMerge((a, b) => a.key === b.key, configA.args, configB.args, overwriteState);
    const possibleTypes = listMerge((a, b) => a.typeName === b.typeName, configA.possibleTypes, listMerge((a, b) => configB.typeName === 'ALLTYPES', configA.possibleTypes, configB.possibleTypes, overwriteState), overwriteState);
    const ofType = configA.ofType && configB.ofType
        ? overwriteState(configA.ofType, configB.ofType)
        : configA.ofType || configB.ofType;
    return {
        ...configA,
        state,
        ...(fields && { fields }),
        ...(possibleTypes && { possibleTypes }),
        ...(ofType && { ofType }),
        ...(args && { args }),
    };
};
export const setDataTypeConfigStates = (configListB) => (configsListA) => {
    const bConfigsMissingFromA = configListB.filter(config => !configsListA.find(c => c.typeName === config.typeName && config.typeName !== 'ALLTYPES'));
    if (bConfigsMissingFromA.length) {
        console.error(`setDataConfigStates: Some configs in B are missing from A. These will not be applied.\n${JSON.stringify(bConfigsMissingFromA.map(c => c.typeName), null, 2)}`);
    }
    return configsListA.map(config => {
        switch (config.kind) {
            case 'SCALAR':
            case 'UNION':
            case 'ENUM':
            case 'OBJECT':
            case 'LIST':
            case 'FIELD':
            case 'INPUT_OBJECT':
            case 'ENUM_VALUE':
            case 'INPUT_VALUE':
                const allTypesConfig = configListB.find(bConfig => bConfig.typeName === 'ALLTYPES');
                const typeNameConfig = configListB.find(bConfig => bConfig.typeName === config.typeName);
                const mergedConfig = allTypesConfig && typeNameConfig
                    ? overwriteState(allTypesConfig, typeNameConfig)
                    : (typeNameConfig ?? allTypesConfig);
                return overwriteState(config, mergedConfig);
            default:
                return config;
        }
    });
};
export const cascadeState = (currentDataTypeConfig, baselineConfigs, inheritanceChainConfigs = []) => {
    const rootConfig = baselineConfigs.find(config => config.typeName === currentDataTypeConfig.typeName);
    const foundRootConfig = rootConfig !== currentDataTypeConfig ? rootConfig : undefined;
    const state = [
        foundRootConfig?.state,
        ...inheritanceChainConfigs.map(config => config.state),
        currentDataTypeConfig.state,
    ].reduce((acc, current) => {
        return current ? Deferred.merge(current)(acc) : acc;
    }, {});
    const fields = currentDataTypeConfig.kind === 'OBJECT' ||
        currentDataTypeConfig.kind === 'INPUT_OBJECT'
        ? currentDataTypeConfig.fields.map(currentFieldConfig => {
            const rootConfigField = foundRootConfig?.fields?.find(item => item.key === currentFieldConfig.key);
            const inheritanceChainFieldConfigs = inheritanceChainConfigs
                .map(config => config.fields?.find(item => item.key === currentFieldConfig.key))
                .filter(i => !!i);
            return cascadeState(currentFieldConfig, baselineConfigs, [
                ...(rootConfigField ? [rootConfigField] : []),
                ...inheritanceChainFieldConfigs,
            ]);
        })
        : undefined;
    const possibleTypes = currentDataTypeConfig.kind === 'UNION'
        ? currentDataTypeConfig.possibleTypes.map(possibleType => {
            const rootConfigPossibleType = foundRootConfig?.possibleTypes?.find(type => type.typeName === possibleType.typeName);
            const inheritanceChainPossibleTypeConfigs = inheritanceChainConfigs
                .map(config => config.possibleTypes?.find(type => type.typeName === possibleType.typeName))
                .filter(i => !!i);
            return cascadeState(possibleType, baselineConfigs, [
                ...(rootConfigPossibleType ? [rootConfigPossibleType] : []),
                ...inheritanceChainPossibleTypeConfigs,
            ]);
        })
        : undefined;
    const ofType = currentDataTypeConfig.kind === 'LIST' ||
        currentDataTypeConfig.kind === 'FIELD' ||
        currentDataTypeConfig.kind === 'INPUT_VALUE'
        ? cascadeState(currentDataTypeConfig.ofType, baselineConfigs, [
            ...inheritanceChainConfigs
                .map(config => config.ofType)
                .filter(i => !!i),
        ])
        : undefined;
    const args = currentDataTypeConfig.kind === 'FIELD' && 'args' in currentDataTypeConfig
        ? currentDataTypeConfig.args.map(currentArgConfig => {
            const rootConfigArg = foundRootConfig?.args?.find(item => item.key === currentArgConfig.key);
            const inheritanceChainArgConfigs = inheritanceChainConfigs
                .map(config => config.args?.find(item => item.key === currentArgConfig.key))
                .filter(i => !!i);
            return cascadeState(currentArgConfig, baselineConfigs, [
                ...(rootConfigArg ? [rootConfigArg] : []),
                ...inheritanceChainArgConfigs,
            ]);
        })
        : undefined;
    return {
        ...currentDataTypeConfig,
        state,
        ...(fields && { fields }),
        ...(possibleTypes && { possibleTypes }),
        ...(ofType && { ofType }),
        ...(args && { args }),
        ...(fields && { fields }),
    };
};
export const cascadeStates = () => (dataTypeConfigs) => {
    return dataTypeConfigs.map(config => {
        return cascadeState(config, dataTypeConfigs);
    });
};
export const removeEqualStateProperties = (inputConfig, baselineConfig) => {
    const inputState = inputConfig.state ?? {};
    const baselineState = baselineConfig.state ?? {};
    const state = Object.fromEntries(Object.entries(inputState).reduce((acc, [key, inputValue]) => {
        const baselineValue = baselineState[key];
        if (isEqual(inputValue, baselineValue)) {
            return acc;
        }
        return [...acc, [key, inputValue]];
    }, []));
    const fields = listMerge((a, b) => a.key === b.key, inputConfig.fields, baselineConfig.fields, removeEqualStateProperties);
    const possibleTypes = listMerge((a, b) => a.typeName === b.typeName, inputConfig.possibleTypes, baselineConfig.possibleTypes, removeEqualStateProperties);
    const ofType = inputConfig.ofType && baselineConfig.ofType
        ? removeEqualStateProperties(inputConfig.ofType, baselineConfig.ofType)
        : inputConfig.ofType || baselineConfig.ofType;
    return {
        ...inputConfig,
        state,
        ...(fields && { fields }),
        ...(possibleTypes && { possibleTypes }),
        ...(ofType && { ofType }),
    };
};
export const pruneStates = (baselineConfigs) => (dataTypeConfigs) => {
    const baseLineConfigsUsed = baselineConfigs ?? dataTypeConfigs;
    return dataTypeConfigs.map(config => {
        const baselineConfig = baseLineConfigsUsed.find(state => state.typeName === config.typeName);
        if (!baselineConfig) {
            return config;
        }
        return removeEqualStateProperties(config, baselineConfig);
    });
};
export const dataTypeConfigToPersistableConfig = (config) => {
    return {
        ...('fields' in config && {
            fields: config.fields?.map(field => dataTypeConfigToPersistableConfig(field)),
        }),
        ...('key' in config && { key: config.key }),
        ...('ofType' in config && {
            ofType: dataTypeConfigToPersistableConfig(config.ofType),
        }),
        ...('possibleTypes' in config && {
            possibleTypes: config.possibleTypes?.map(possibleType => dataTypeConfigToPersistableConfig(possibleType)),
        }),
        ...('state' in config && { state: config.state }),
        ...('typeName' in config && { typeName: config.typeName }),
    };
};
export const dataTypeConfigsToPersistableConfigs = () => (dataTypeConfigs) => {
    return dataTypeConfigs.map(dataTypeConfigToPersistableConfig);
};
export const prunePersistableConfigs = () => (persistableConfigs) => {
    if (!persistableConfigs) {
        return undefined;
    }
    if (Array.isArray(persistableConfigs)) {
        const configs = persistableConfigs
            .map(config => prunePersistableConfigs()(config))
            .filter(item => !!item);
        return objectHasKeysOrUndefined(configs);
    }
    const finalState = objectHasKeysOrUndefined(persistableConfigs.state);
    const finalFields = objectHasKeysOrUndefined(prunePersistableConfigs()('fields' in persistableConfigs ? persistableConfigs.fields : []));
    const finalPossibleTypes = objectHasKeysOrUndefined(prunePersistableConfigs()('possibleTypes' in persistableConfigs
        ? persistableConfigs.possibleTypes
        : []));
    const finalOfType = objectHasKeysOrUndefined(prunePersistableConfigs()('ofType' in persistableConfigs ? persistableConfigs.ofType : undefined));
    const hasState = Boolean(finalState || finalFields || finalPossibleTypes || finalOfType);
    if (!hasState) {
        return undefined;
    }
    return {
        ...persistableConfigs,
        ...(finalState && { state: finalState }),
        ...(finalFields && { fields: finalFields }),
        ...(finalPossibleTypes && { possibleTypes: finalPossibleTypes }),
        ...(finalOfType && { ofType: finalOfType }),
    };
};
export const cascadePrune = (currentDataTypeConfig, baselineConfigs, inheritanceChainConfigs = []) => {
    const rootConfig = baselineConfigs.find(state => state.typeName === currentDataTypeConfig.typeName);
    const foundRootConfig = rootConfig !== currentDataTypeConfig ? rootConfig : undefined;
    const inheritedState = [
        foundRootConfig?.state,
        ...inheritanceChainConfigs.map(config => config.state),
    ].reduce((acc, current) => {
        return current ? Deferred.merge(current)(acc) : acc;
    }, {});
    const state = currentDataTypeConfig.state
        ? Object.fromEntries(Object.entries(currentDataTypeConfig.state).filter(([key, value]) => {
            return !isEqual(value, inheritedState[key]);
        }))
        : {};
    const fields = 'fields' in currentDataTypeConfig && currentDataTypeConfig.fields
        ? currentDataTypeConfig.fields.map(currentFieldConfig => {
            const rootConfigField = foundRootConfig?.fields?.find(item => item.key === currentFieldConfig.key);
            const inheritanceChainFieldConfigs = inheritanceChainConfigs
                .map(config => config.fields?.find(item => item.key === currentFieldConfig.key))
                .filter(i => !!i);
            return cascadePrune(currentFieldConfig, baselineConfigs, [
                ...(rootConfigField ? [rootConfigField] : []),
                ...inheritanceChainFieldConfigs,
            ]);
        })
        : undefined;
    const args = 'args' in currentDataTypeConfig && currentDataTypeConfig.args
        ? currentDataTypeConfig.args.map(currentArgConfig => {
            const rootConfigArg = foundRootConfig?.args?.find(item => item.key === currentArgConfig.key);
            const inheritanceChainArgConfigs = inheritanceChainConfigs
                .map(config => config.args?.find(item => item.key === currentArgConfig.key))
                .filter(i => !!i);
            return cascadePrune(currentArgConfig, baselineConfigs, [
                ...(rootConfigArg ? [rootConfigArg] : []),
                ...inheritanceChainArgConfigs,
            ]);
        })
        : undefined;
    const possibleTypes = 'possibleTypes' in currentDataTypeConfig &&
        currentDataTypeConfig.possibleTypes
        ? currentDataTypeConfig.possibleTypes.map(possibleType => {
            const rootConfigPossibleType = foundRootConfig?.possibleTypes?.find(type => type.typeName === possibleType.typeName);
            const inheritanceChainPossibleTypeConfigs = inheritanceChainConfigs
                .map(config => config.possibleTypes?.find(type => type.typeName === possibleType.typeName))
                .filter(i => !!i);
            return cascadePrune(possibleType, baselineConfigs, [
                ...(rootConfigPossibleType ? [rootConfigPossibleType] : []),
                ...inheritanceChainPossibleTypeConfigs,
            ]);
        })
        : undefined;
    const ofType = 'ofType' in currentDataTypeConfig && currentDataTypeConfig.ofType
        ? cascadePrune(currentDataTypeConfig.ofType, baselineConfigs, [
            ...inheritanceChainConfigs
                .map(config => config.ofType)
                .filter(i => !!i),
        ])
        : undefined;
    return {
        ...currentDataTypeConfig,
        state,
        ...(fields && { fields }),
        ...(possibleTypes && { possibleTypes }),
        ...(ofType && { ofType }),
        ...(args && { args }),
        ...(fields && { fields }),
    };
};
export const reverseCascadeStates = () => (dataTypeConfigs) => dataTypeConfigs.map(config => cascadePrune(config, dataTypeConfigs));
const objectHasKeysOrUndefined = (value) => !!value && Object.keys(value).length > 0 ? value : undefined;
const setDefaultStates = () => (config) => {
    if (Array.isArray(config)) {
        return config.map(config => setDefaultStates()(config));
    }
    let label = config.typeName;
    if (config.kind === 'TYPE_REFERENCE')
        return config;
    if ('key' in config) {
        label = config.key;
    }
    if (config.kind === 'LIST') {
        label = `${config.ofType.typeName} List`;
    }
    const newConfig = {
        ...config,
        state: Deferred.merge({ label })(config.state ?? {}),
    };
    return pipe(newConfig, mapDataTypeConfigChildren(setDefaultStates()));
};
export const findAll = (locator) => (config) => {
    const results = [];
    const findMatches = (currentConfig, currentSelectors) => {
        if (Array.isArray(currentConfig)) {
            currentConfig.forEach(item => findMatches(item, currentSelectors));
            return;
        }
        if (currentSelectors.length === 0) {
            results.push(currentConfig);
            return;
        }
        const [currentSelector, ...remainingSelectors] = currentSelectors;
        const [key, value] = currentSelector;
        if (key === 'typeName' && currentConfig.typeName === value) {
            findMatches(currentConfig, remainingSelectors);
        }
        else if (key === 'key' &&
            'key' in currentConfig &&
            currentConfig.key === value) {
            findMatches(currentConfig, remainingSelectors);
        }
        else if (key === 'kind' && currentConfig.kind === value) {
            findMatches(currentConfig, remainingSelectors);
        }
        else if (key === 'fields' &&
            'fields' in currentConfig &&
            Array.isArray(currentConfig.fields)) {
            findMatches(currentConfig.fields, [
                ['key', value],
                ...remainingSelectors,
            ]);
        }
        if ('fields' in currentConfig && currentConfig.fields) {
            findMatches(currentConfig.fields, currentSelectors);
        }
        if ('possibleTypes' in currentConfig && currentConfig.possibleTypes) {
            findMatches(currentConfig.possibleTypes, currentSelectors);
        }
        if ('ofType' in currentConfig && currentConfig.ofType) {
            findMatches(currentConfig.ofType, currentSelectors);
        }
        if ('items' in currentConfig && currentConfig.items) {
            findMatches(currentConfig.items, currentSelectors);
        }
        if ('fields' in currentConfig && currentConfig.fields) {
            findMatches(currentConfig.fields, currentSelectors);
        }
    };
    findMatches(config, locator);
    return results;
};
const matchSpecificity = (configsB, _baseConfigs) => (configsA) => {
    const baseConfigs = _baseConfigs ?? configsA;
    if (Array.isArray(configsB) && Array.isArray(configsA)) {
        return configsB.reduce((acc, configB) => {
            const selectorPointer = getDataTypeSelectorPointer(configB);
            return acc.map(configA => {
                if (configMatchesMatcher(selectorPointer)(configA)) {
                    return matchSpecificity(configB, baseConfigs)(configA);
                }
                return configA;
            });
        }, configsA);
    }
    if (Array.isArray(configsB) || Array.isArray(configsA)) {
        throw new Error('matchSpecificity only works with configs that match types. Received an array and a single value.');
    }
    const bSelectorPointer = getDataTypeSelectorPointer(configsB);
    const matches = configMatchesMatcher(bSelectorPointer)(configsA);
    if (!matches) {
        console.log("matchSpecificity: no match Shouldn't have ended up here");
        return configsA;
    }
    let currentConfig = (configsA.kind === 'TYPE_REFERENCE'
        ?
            baseConfigs.find(config => config.typeName === configsA.typeName)
        : configsA);
    if (!currentConfig) {
        throw new Error(`matchSpecificity: no match. Couldn't find a base config for ${configsA.typeName}`);
    }
    if ('fields' in currentConfig && configsB.fields) {
        currentConfig.fields = matchSpecificity(configsB.fields, baseConfigs)(currentConfig.fields);
    }
    if ('possibleTypes' in currentConfig && configsB.possibleTypes) {
        currentConfig.possibleTypes = matchSpecificity(configsB.possibleTypes, baseConfigs)(currentConfig.possibleTypes);
    }
    if ('ofType' in currentConfig && configsB.ofType) {
        currentConfig.ofType = matchSpecificity(configsB.ofType, baseConfigs)(currentConfig.ofType);
    }
    if ('items' in currentConfig &&
        'items' in configsB &&
        configsB.items) {
        currentConfig.items = matchSpecificity(configsB.items, baseConfigs)(currentConfig.items);
    }
    if ('args' in currentConfig && configsB.args) {
        currentConfig.args = matchSpecificity(configsB.args, baseConfigs)(currentConfig.args);
    }
    if ('fields' in currentConfig && configsB.fields) {
        currentConfig.fields = matchSpecificity(configsB.fields, baseConfigs)(currentConfig.fields);
    }
    return currentConfig;
};
export const pruneConfigsForSave = (baselineConfigs) => (configs) => {
    return pipe(configs, pruneStates(baselineConfigs), reverseCascadeStates(), dataTypeConfigsToPersistableConfigs(), prunePersistableConfigs());
};
const findByTypeReference = (baseConfigs) => (refConfig) => {
    return baseConfigs.find(config => config.typeName === refConfig.typeName);
};
const replaceTypeRefsUntilCircular = (depth = 11, _baseConfigs) => (configs) => {
    const baseConfigs = _baseConfigs ?? (Array.isArray(configs) ? configs : [configs]);
    const processType = (config, visitedTypes = new Set(), currentDepth = 0) => {
        if (currentDepth >= depth) {
            return config;
        }
        if (config.kind === 'TYPE_REFERENCE') {
            if (visitedTypes.has(config.typeName)) {
                return config;
            }
            const referencedType = baseConfigs.find(c => c.typeName === config.typeName);
            if (!referencedType) {
                return config;
            }
            const newVisited = new Set(visitedTypes).add(config.typeName);
            return processType({ ...referencedType }, newVisited, currentDepth);
        }
        const newVisitedTypes = new Set(visitedTypes).add(config.typeName);
        if ('fields' in config && config.fields) {
            config = {
                ...config,
                fields: config.fields.map(field => processType(field, newVisitedTypes, currentDepth + 1)),
            };
        }
        if ('possibleTypes' in config && config.possibleTypes) {
            config = {
                ...config,
                possibleTypes: config.possibleTypes.map(type => processType(type, newVisitedTypes, currentDepth + 1)),
            };
        }
        if ('ofType' in config && config.ofType) {
            config = {
                ...config,
                ofType: processType(config.ofType, newVisitedTypes, currentDepth + 1),
            };
        }
        return config;
    };
    if (Array.isArray(configs)) {
        return configs.map(config => processType(config));
    }
    return processType(configs);
};
const convertGraphQLSchemaToDataTypeConfigs = () => (schema) => pipe(schema, schemaToDefinitions, applyDataTypeSelectorPaths(), setDefaultStates());
const renderGraphQLQueryFields = () => (fields, indent = 2, depth = 10) => {
    if (!fields || fields.length === 0) {
        return '';
    }
    if (depth === 0) {
        return '';
    }
    return fields
        .map(field => {
        if (field.state?.hidden) {
            return '';
        }
        const key = field.key;
        const spaces = ' '.repeat(indent);
        if (field.kind === 'FIELD') {
            const ofType = field.ofType;
            if (ofType.kind === 'SCALAR') {
                return `${spaces}${key}`;
            }
            if (ofType.kind === 'LIST') {
                const listOfType = ofType.ofType;
                if (listOfType.kind === 'SCALAR') {
                    return `${spaces}${key}`;
                }
                if (listOfType.kind === 'OBJECT' && listOfType.fields) {
                    const nestedFields = renderGraphQLQueryFields()(listOfType.fields, indent + 2, depth - 1);
                    if (nestedFields.replace(/\s/g, '') === '')
                        return '';
                    return `${spaces}${key} {\n${nestedFields}\n${spaces}}`;
                }
            }
            if (ofType.kind === 'OBJECT' && ofType.fields) {
                const nestedFields = renderGraphQLQueryFields()(ofType.fields, indent + 2, depth - 1);
                if (nestedFields.replace(/\s/g, '') === '')
                    return '';
                return `${spaces}${key} {\n${nestedFields}\n${spaces}}`;
            }
            if (ofType.kind === 'UNION' && ofType.possibleTypes) {
                const fragments = ofType.possibleTypes
                    .map(type => {
                    if (type.kind === 'OBJECT' && type.fields) {
                        const typeFields = renderGraphQLQueryFields()(type.fields, indent + 4, depth - 1);
                        return `${spaces}  ... on ${type.typeName} {\n${typeFields}\n${spaces}  }`;
                    }
                    return '';
                })
                    .filter(Boolean)
                    .join('\n');
                return `${spaces}${key} {\n${spaces}  __typename\n${fragments}\n${spaces}}`;
            }
        }
        return `${spaces}${key}`;
    })
        .filter(Boolean)
        .join('\n');
};
const prunedStateConfigToStateConfig = (baseDataTypes) => (prunedStateConfig) => {
    const convertToStateConfig = (prunedStateConfig, currentLevelBaseTypes) => {
        const matchingConfig = StateConfigUtils.findMatchingStateConfig(currentLevelBaseTypes)(prunedStateConfig);
        if (!matchingConfig) {
            return null;
        }
        const baseConfig = matchingConfig?.kind === 'TYPE_REFERENCE'
            ? findByTypeReference(baseDataTypes)(matchingConfig)
            : matchingConfig;
        if (!baseConfig) {
            throw new Error(`No Type Reference config found for ${JSON.stringify(matchingConfig)}`);
        }
        const hydratedStateConfig = {
            typeName: baseConfig.typeName,
            ...('key' in baseConfig && { key: baseConfig.key }),
            ...('name' in baseConfig && { name: baseConfig.name }),
            state: prunedStateConfig.state ?? {},
        };
        if ('fields' in prunedStateConfig &&
            prunedStateConfig.fields &&
            'fields' in baseConfig) {
            ;
            hydratedStateConfig.fields =
                prunedStateConfig.fields.map(field => convertToStateConfig(field, baseConfig.fields));
        }
        if ('possibleTypes' in prunedStateConfig &&
            prunedStateConfig.possibleTypes &&
            'possibleTypes' in baseConfig) {
            ;
            hydratedStateConfig.possibleTypes =
                prunedStateConfig.possibleTypes.map(type => convertToStateConfig(type, baseConfig.possibleTypes));
        }
        if ('enumValues' in prunedStateConfig &&
            prunedStateConfig.enumValues &&
            'enumValues' in baseConfig) {
            ;
            hydratedStateConfig.enumValues =
                prunedStateConfig.enumValues.map(value => convertToStateConfig(value, baseConfig.enumValues));
        }
        if ('fields' in prunedStateConfig &&
            prunedStateConfig.fields &&
            'fields' in baseConfig) {
            ;
            hydratedStateConfig.fields =
                prunedStateConfig.fields.map(field => convertToStateConfig(field, baseConfig.fields));
        }
        if ('args' in prunedStateConfig &&
            prunedStateConfig.args &&
            'args' in baseConfig) {
            ;
            hydratedStateConfig.args =
                prunedStateConfig.args.map(arg => convertToStateConfig(arg, baseConfig.args));
        }
        if ('ofType' in prunedStateConfig &&
            prunedStateConfig.ofType &&
            'ofType' in baseConfig) {
            ;
            hydratedStateConfig.ofType =
                convertToStateConfig(prunedStateConfig.ofType, baseConfig.ofType);
        }
        return hydratedStateConfig;
    };
    return prunedStateConfig
        .map(config => convertToStateConfig(config, baseDataTypes))
        .filter(Boolean);
};
const resolveTypeReference = (rootDataTypeConfigs, config) => {
    if (config.kind === 'TYPE_REFERENCE') {
        const foundType = findByTypeReference(rootDataTypeConfigs)(config);
        if (!foundType) {
            throw new Error(`No type reference found for ${JSON.stringify(config)}`);
        }
        return foundType;
    }
    return config;
};
const tryOrNull = (func) => {
    try {
        return func();
    }
    catch (error) {
        return null;
    }
};
const createSelfComputingConfigs = (stateConfigs) => (dataTypeConfigs) => {
    const createSelfComputingConfig = (config, getParentStateConfigs, parentPath = []) => {
        let getInheritanceConfigsCache;
        const currentPath = [
            ...parentPath,
            getDataTypeSelectorPointer(config),
        ];
        const getInheritanceConfigs = () => {
            if (getInheritanceConfigsCache) {
                return getInheritanceConfigsCache;
            }
            const foundRootConfig = StateConfigUtils.findMatchingStateConfig(stateConfigs)(config);
            const foundInheritedConfigs = getParentStateConfigs()
                .map(parentStateConfig => {
                const allTypesMatch = tryOrNull(() => findDataTypeConfigBreadthFirst([['typeName', 'ALLTYPES']])(parentStateConfig));
                const selector = [getDataTypeSelectorPointer(config)];
                const foundMatchToCurrent = tryOrNull(() => findDataTypeConfigBreadthFirst(selector)(parentStateConfig));
                return [
                    ...(allTypesMatch ? [allTypesMatch] : []),
                    ...(foundMatchToCurrent ? [foundMatchToCurrent] : []),
                ];
            })
                .flat();
            const newParentStateConfigs = [
                ...(foundRootConfig ? [foundRootConfig] : []),
                ...(foundInheritedConfigs?.length > 0 ? foundInheritedConfigs : []),
            ];
            getInheritanceConfigsCache = newParentStateConfigs;
            return newParentStateConfigs;
        };
        const selfComputingConfig = {
            ...config,
            meta: {
                ...config.meta,
                path: currentPath,
                unmappedPath: currentPath,
            },
        };
        if (config.kind === 'LIST') {
            Deferred.setField('ofType', () => createSelfComputingConfig(resolveTypeReference(dataTypeConfigs, config.ofType), getInheritanceConfigs, currentPath))(selfComputingConfig);
        }
        if (config.kind === 'OBJECT') {
            Deferred.setField('fields', () => config.fields.map(field => createSelfComputingConfig(field, getInheritanceConfigs, currentPath)))(selfComputingConfig);
        }
        if (config.kind === 'UNION') {
            Deferred.setField('possibleTypes', () => config.possibleTypes.map(config => createSelfComputingConfig(config, getInheritanceConfigs, currentPath)))(selfComputingConfig);
        }
        if (config.kind === 'INPUT_OBJECT') {
            Deferred.setField('fields', () => config.fields.map(field => createSelfComputingConfig(field, getInheritanceConfigs, currentPath)))(selfComputingConfig);
        }
        if (config.kind === 'FIELD') {
            Deferred.setField('args', () => config.args.map(config => createSelfComputingConfig(config, getInheritanceConfigs, currentPath)))(selfComputingConfig);
            Deferred.setField('ofType', () => createSelfComputingConfig(resolveTypeReference(dataTypeConfigs, config.ofType), getInheritanceConfigs, currentPath))(selfComputingConfig);
        }
        if (config.kind === 'ENUM') {
            Deferred.setField('enumValues', () => config.enumValues.map(config => createSelfComputingConfig(config, getInheritanceConfigs, currentPath)))(selfComputingConfig);
        }
        if (config.kind === 'INPUT_VALUE') {
            Deferred.setField('ofType', () => createSelfComputingConfig(resolveTypeReference(dataTypeConfigs, config.ofType), getInheritanceConfigs, currentPath))(selfComputingConfig);
        }
        Deferred.setField('state', () => {
            const state = getInheritanceConfigs().reduce((acc, current) => Deferred.merge(current.state ?? {})(acc), {});
            if (selfComputingConfig.kind === 'INPUT_VALUE' && !state.label) {
                state.label = selfComputingConfig.key;
            }
            if (selfComputingConfig.kind === 'INPUT_OBJECT' && !state.label) {
                state.label = selfComputingConfig.typeName;
            }
            if (selfComputingConfig.kind === 'ENUM' && !state.label) {
                state.label = selfComputingConfig.typeName;
            }
            if (selfComputingConfig.kind === 'FIELD' && !state.label) {
                state.label = selfComputingConfig.key;
            }
            if (selfComputingConfig.kind === 'OBJECT' &&
                !Object.hasOwn(state, '_tableColumns')) {
                Deferred.setField('_tableColumns', () => {
                    return {
                        columns: selfComputingConfig.fields.map(field => {
                            return {
                                key: field.key,
                            };
                        }),
                    };
                })(state);
            }
            if (selfComputingConfig.kind === 'LIST' &&
                !Object.hasOwn(state, '_tableColumns')) {
                if (selfComputingConfig.ofType.kind === 'OBJECT') {
                    const ofTypeState = selfComputingConfig.ofType.state;
                    if (ofTypeState && Object.hasOwn(ofTypeState, '_tableColumns')) {
                        const tableStateDescriptor = Object.getOwnPropertyDescriptor(ofTypeState, '_tableColumns');
                        if (tableStateDescriptor) {
                            Object.defineProperty(state, '_tableColumns', tableStateDescriptor);
                        }
                    }
                }
            }
            return state;
        })(selfComputingConfig);
        if (config.kind === 'TYPE_REFERENCE') {
            throw new Error('Type reference not supported');
        }
        return selfComputingConfig;
    };
    return dataTypeConfigs.map(config => createSelfComputingConfig(config, () => [], []));
};
const dataTypeToStateConfig = (dataTypeConfig) => {
    const stateConfig = Object.fromEntries(Object.entries(dataTypeConfig)
        .filter(([key, value]) => {
        return [
            'kind',
            'typeName',
            'key',
            'name',
            'state',
            'fields',
            'args',
            'ofType',
            'possibleTypes',
            'enumValues',
            'fields',
            'items',
        ].includes(key);
    })
        .map(([key, value]) => {
        if (value?.kind === 'TYPE_REFERENCE') {
            return undefined;
        }
        if (key === 'fields') {
            return [
                'fields',
                value.map(inputField => dataTypeToStateConfig(inputField)),
            ];
        }
        if (key === 'args') {
            return ['args', value?.map(arg => dataTypeToStateConfig(arg))];
        }
        if (key === 'fields') {
            return ['fields', value?.map(field => dataTypeToStateConfig(field))];
        }
        if (key === 'ofType') {
            return ['ofType', dataTypeToStateConfig(value)];
        }
        if (key === 'items') {
            return ['items', value?.map(item => dataTypeToStateConfig(item))];
        }
        if (key === 'possibleTypes') {
            return [
                'possibleTypes',
                value.map(type => dataTypeToStateConfig(type)),
            ];
        }
        if (key === 'enumValues') {
            return [
                'enumValues',
                value.map(value => dataTypeToStateConfig(value)),
            ];
        }
        return [key, value];
    })
        .filter(([key, value]) => {
        return value !== undefined;
    }));
    return stateConfig;
};
const updateState = (updater) => config => {
    const newState = typeof updater === 'function' ? updater(config) : updater;
    return {
        ...config,
        state: Deferred.merge(newState)(config.state ?? {}),
    };
};
const toDebugObject = (include = [
    'kind',
    'key',
    'typeName',
    'ofType',
    'fields',
    'args',
    'fields',
    'fields',
], depthLimit = 10) => (dataTypeConfig) => {
    const processConfig = (config, currentDepth) => {
        if (depthLimit !== false && currentDepth >= depthLimit) {
            return '[depth limit reached]';
        }
        if (Array.isArray(config)) {
            return config.map(item => processConfig(item, currentDepth));
        }
        const result = {};
        for (const key of include) {
            if (key in config) {
                const value = config[key];
                if (value &&
                    typeof value === 'object' &&
                    'kind' in value &&
                    typeof value.kind === 'string') {
                    result[key] = processConfig(value, currentDepth + 1);
                }
                else if (Array.isArray(value)) {
                    if (value.length > 0 &&
                        typeof value[0] === 'object' &&
                        'kind' in value[0]) {
                        result[key] = processConfig(value, currentDepth + 1);
                    }
                    else {
                        result[key] = value;
                    }
                }
                else {
                    result[key] = value;
                }
            }
        }
        return result;
    };
    return processConfig(dataTypeConfig, 0);
};
const isKind = (kind) => (value) => {
    if (typeof value === 'object' &&
        value !== null &&
        'kind' in value &&
        value.kind === kind) {
        return true;
    }
    return false;
};
const hasFields = (config) => {
    return ((isKind('OBJECT')(config) || isKind('INPUT_OBJECT')(config)) &&
        'fields' in config &&
        Array.isArray(config.fields));
};
const getFields = () => (config) => pipe(config, Assert.orThrow(Value.isNotNullable), Assert.orThrow(hasFields), config => config.fields);
const hasOfType = (config) => {
    return ((isKind('FIELD')(config) ||
        isKind('INPUT_VALUE')(config) ||
        isKind('LIST')(config)) &&
        'ofType' in config &&
        config.ofType !== undefined &&
        config.ofType !== null);
};
const getOfType = () => (config) => pipe(config, Assert.orThrow(Value.isNotNullable), Assert.orThrow(hasOfType), config => config.ofType);
const hasKey = (config) => {
    return ((isKind('FIELD')(config) || isKind('INPUT_VALUE')(config)) &&
        'key' in config &&
        typeof config.key === 'string');
};
const getKey = () => (config) => pipe(config, Assert.orThrow(Value.isNotNullable), Assert.orThrow(hasKey), config => config.key);
const getState = () => (config) => pipe(config, Assert.orThrow(Value.isNotNullable), config => config.state);
export const DataTypeConfigUtils = {
    updateState,
    pruneStates,
    reverseCascadeStates,
    toPersistableConfig: dataTypeConfigsToPersistableConfigs,
    prunePersistableConfigs,
    setDefaultStates,
    listMerge,
    createSelfComputingConfigs,
    prunedStateToStateConfig: prunedStateConfigToStateConfig,
    find: findDataTypeConfigBreadthFirst,
    getFields,
    getField: getDataTypeConfigField,
    getOfType,
    getKey,
    getState,
    isKind,
    convertGraphQLSchemaToDataTypeConfigs,
    renderGraphQLQueryFields,
    overwriteState,
    mapDataTypeConfig: mapDataTypeConfigLazy,
    mapDataTypeConfigLazy,
    createDataTypeConfigFromMapper,
    createObjectMapperConfig,
    createInputObjectMapperConfig,
    createFieldMapperConfig,
    createInputValueMapperConfig,
    mapInputObjectConfig,
    toDebugObject,
    validateData,
    toZodSchema,
};
//# sourceMappingURL=DataTypeConfig.utils.js.map